import { PensionPlanTransfer } from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { esDgsfpReal } from '../types/individualDashboard';
import { isPlatformTransfer } from './resolvePrimaryTransfer';

/**
 * Estados en los que una petición sigue VIVA, copia de `LIVE_TRANSFER_STATUSES`
 * (comparsa, `pension-plan-transfers/utils/is-funnel-request.ts:12`).
 *
 * Lista blanca y no negra, por el mismo motivo que allí: `status` puede venir vacío, y con una
 * lista de exclusiones tanto ese hueco como cualquier estado que se añada mañana entrarían como
 * firmables.
 */
const ESTADOS_VIVOS = ['draft', 'requested', 'in_progress'];

/**
 * ¿Comparsa aceptaría firmar el boletín de este traspaso?
 *
 * Las CINCO puertas de `kyc.service.ts:314-343`, en un solo sitio. Existe porque estaban escritas
 * dos veces y no coincidían: la tarjeta de arriba del dashboard comprobaba cuatro y el paso
 * «Firmar» del ProcessTimeline solo dos (plataforma + destino elegido). El resultado era que, con
 * el KYC sin aprobar o con el DGSFP centinela, la tarjeta no decía nada y unos centímetros más
 * abajo había un botón «Firmar» que devolvía 400.
 *
 * Que la interfaz prometa exactamente lo que el backend acepta no es una comodidad: es la
 * diferencia entre un botón y una trampa. Y con la regla en dos sitios, arreglar uno no arregla
 * el otro — que es justo lo que había pasado.
 *
 * 🔴 La primera puerta es el ESTADO, y es la que se olvidó al escribir esto la primera vez: la
 * tarjeta no la echaba en falta porque ya sale solo para traspasos vivos, pero el timeline pinta
 * también los cerrados. Un traspaso que la gestora rechazó, con todo lo demás en regla, ofrecía
 * «Firmar» y devolvía «Transfer request is not open for signature». Firmar el boletín de una
 * operación muerta —o de uno cuyo dinero ya se movió— no documenta nada.
 *
 * ⚠️ `isPlatformTransfer` NO es puerta del backend: comparsa firmaría igual una petición del
 * funnel. Está aquí a propósito, porque esa gente ya firma dentro de su propia verificación y
 * ofrecérselo otra vez sería pedirles dos veces lo mismo. O sea: esto es igual de estricto que el
 * backend en las cinco que él pone, y una más por decisión nuestra.
 *
 * ⚠️ NO incluye «ya está firmado»: eso tampoco es una puerta del backend, es el estado de este
 * traspaso, y cada sitio lo usa distinto. La tarjeta solo aparece si NO está firmado; el timeline
 * enseña el hito cumplido cuando sí lo está. Por eso lo decide quien llama, no esta función.
 */
export function puedeFirmarBoletin(
	transfer: PensionPlanTransfer | null | undefined,
	kycStatus: string | null | undefined
): boolean {
	if (!transfer) return false;

	return (
		ESTADOS_VIVOS.includes(transfer.status ?? '') &&
		kycStatus === 'APPROVED' &&
		isPlatformTransfer(transfer) &&
		transfer.kind === 'EXTERNAL_TRANSFER' &&
		esDgsfpReal(transfer.DGSFP_code) &&
		!!transfer.selectedArcaPlanId
	);
}
